import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, DatePicker, Form, Modal, Select } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { ListPage } from "../../components/ListPage";
import { DocumentStatusTag } from "../../components/DocumentStatusTag";
import { FilterToggle } from "../../components/FilterToggle";
import { ProjectLink } from "../../components/ProjectLink";
import { SiteLabel } from "../../components/SiteLabel";
import { SiteSelect } from "../../components/SiteSelect";
import { useApi } from "../../helpers/useApi";
import { useSitesQuery } from "../../helpers/queries";
import { hasAnyGrant } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import { DT_FORMATS } from "../../helpers/constants";
import type { AttendanceSheet } from "../../api-models";

const STATUSES = ["DRAFT", "SUBMITTED", "RETURNED_FOR_CORRECTION", "HR_APPROVED", "LOCKED", "REOPENED", "CANCELLED"];

export default function AttendanceSheetsListPage() {
  const { user } = useUser();
  const navigate = useNavigate();
  const canWrite = hasAnyGrant(user, "Attendance.Write");
  const { request, loading } = useApi();
  const { data: sites = [] } = useSitesQuery();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  async function createSheet() {
    const values = await form.validateFields();
    const created = await request<AttendanceSheet>({
      method: "POST",
      url: "/attendance-sheets",
      data: { siteId: values.siteId, attendanceDate: values.attendanceDate.format("YYYY-MM-DD") },
    });
    setOpen(false);
    navigate(`/attendance/${created.id}`);
  }

  return (
    <>
      <ListPage<AttendanceSheet>
        title="Daily Attendance"
        endpoint="/attendance-sheets"
        searchPlaceholder="Search doc no or remarks..."
        linkTo={(r) => `/attendance/${r.id}`}
        actions={
          canWrite && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              data-tour="attendance-new"
              onClick={() => {
                form.resetFields();
                setOpen(true);
              }}
            >
              New attendance sheet
            </Button>
          )
        }
        filters={(controls) => (
          <>
            <Select
              allowClear
              placeholder="Status"
              style={{ width: 200 }}
              options={STATUSES.map((s) => ({ value: s, label: s.replaceAll("_", " ") }))}
              value={controls.value("status")}
              onChange={(v) => controls.set("status", v)}
            />
            {/* Both dashboard attendance tiles land here. */}
            <FilterToggle name="pendingSubmission" label="Not submitted" controls={controls} />
            <FilterToggle name="hasExceptions" label="Has exceptions" controls={controls} />
          </>
        )}
        columns={[
          { title: "Doc No", dataIndex: "docNo", sorter: true },
          { title: "Site", render: (_, r) => r.site?.name },
          { title: "Project", render: (_, r) => <ProjectLink project={r.project} /> },
          { title: "Date", dataIndex: "attendanceDate", sorter: true, render: (v) => dayjs(v).format(DT_FORMATS.date) },
          { title: "Calendar", dataIndex: "classification", sorter: true, render: (v) => String(v).replaceAll("_", " ") },
          { title: "Status", dataIndex: "status", sorter: true, render: (v) => <DocumentStatusTag status={v} /> },
        ]}
        renderCard={(r) => (
          <div>
            <strong>{r.docNo}</strong>
            <div>
              <SiteLabel site={r.site} project={r.project} /> — {dayjs(r.attendanceDate).format(DT_FORMATS.date)}
            </div>
          </div>
        )}
      />
      <Modal title="New attendance sheet" open={open} onCancel={() => setOpen(false)} onOk={createSheet} confirmLoading={loading} destroyOnHidden>
        <Form form={form} layout="vertical">
          <Form.Item name="siteId" label="Site" rules={[{ required: true }]}>
            <SiteSelect showSearch optionFilterProp="label" options={sites.map((s) => ({ value: s.id, label: s.name }))} />
          </Form.Item>
          <Form.Item name="attendanceDate" label="Attendance date" rules={[{ required: true }]} initialValue={dayjs()}>
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
